import { useState } from 'react';

import {
  STOREFRONT_PATHS,
  type NewServicePremiseType,
  type ServiceAddress,
} from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, Input, Label, Radio, RadioGroup } from '@fluxora/ui';

import { useNewServiceDeclareMutation } from '../api/new-service.queries';
import {
  NEW_SERVICE_PREMISE_TYPES,
  premiseTypeLabelKey,
  scalesByUnitCount,
  toBuildFacts,
  toServiceAddress,
  validateNewServiceForm,
  type NewServiceFormError,
} from '../services/newService.service';

/**
 * E-SF20 / US-01 — "this address has no meter yet, tell us about the build".
 *
 * ── IT IS THE SECOND OFFER ON THE not_found SCREEN, NOT THE FIRST ──────────
 * `AddressPage` renders the re-check message above this, and this panel starts
 * COLLAPSED behind an explicit "it's a new build" affordance. The ordering is the
 * duplicate-declaration guard (US-01 AC2), not a layout preference: an in-territory
 * address that matches nothing is far more often a typo than a new construction,
 * and raising a duplicate declaration for a premise that already has service is
 * the expensive, common error in this scenario. The customer has to say the
 * address is right before they can say it is new.
 *
 * ── HONEST COPY (US posture) ───────────────────────────────────────────────
 * The confirmation promises exactly what the system does: the declaration is
 * recorded and the customer gets a link to check back. It does NOT promise that
 * we will notify them when the meter goes live — the detection loop (US-02) and
 * the notification (US-13) are not built, so a "we'll email you when it's ready"
 * would be a customer-facing lie of the kind E-SF19 was written to clear out.
 * TODO(E-SF20 US-02/US-13): once detection + notify land, say so here.
 */
export interface NewServiceDeclarationProps {
  /** The ZIP the customer already chose. Always known; never re-asked. */
  readonly zip: string;
  /**
   * A resolved address, when the on-ramp already has one.
   *
   * ── WHY IT IS OPTIONAL ─────────────────────────────────────────────────
   * The two on-ramps arrive here holding different things. The address on-ramp
   * has a full `ServiceAddress` the lookup just failed to match. The ZIP on-ramp
   * has only a ZIP — and the reason the customer is here at all is that their
   * address is not in the suggestion list, which for a brand-new premise it never
   * can be: the list is built from ESI IDs, and this premise has none. So on that
   * path the street has to be typed, and this component collects it.
   */
  readonly address?: ServiceAddress;
}

export function NewServiceDeclaration({ zip, address }: NewServiceDeclarationProps): JSX.Element {
  const { t } = useTranslation();
  const mutation = useNewServiceDeclareMutation();

  const collectsAddress = address === undefined;

  const [open, setOpen] = useState(false);
  const [line1, setLine1] = useState('');
  const [city, setCity] = useState('');
  const [premiseType, setPremiseType] = useState<NewServicePremiseType | ''>('');
  const [squareFeet, setSquareFeet] = useState('');
  const [unitCount, setUnitCount] = useState('');
  const [expectedOccupancyDate, setExpectedOccupancyDate] = useState('');
  const [permitNumber, setPermitNumber] = useState('');
  const [builderName, setBuilderName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<NewServiceFormError | undefined>(undefined);

  const form = {
    line1,
    city,
    premiseType,
    squareFeet,
    unitCount,
    expectedOccupancyDate,
    permitNumber,
    builderName,
    email,
  };

  const handleSubmit = (): void => {
    const invalid = validateNewServiceForm(form, { collectsAddress });
    setError(invalid ?? undefined);
    if (invalid !== null) return;
    mutation.mutate({
      address: address ?? toServiceAddress(form, zip),
      buildFacts: toBuildFacts(form),
      email: email.trim(),
    });
  };

  if (mutation.isSuccess) {
    // The link the customer keeps. Built from THIS origin rather than echoed from
    // the server: the BFF composes its own copy for the email from operator
    // config (never a Host header — see pending-service-link.ts), and the browser
    // already knows where it is, so neither has to trust the other's idea of it.
    const statusUrl = new URL(
      STOREFRONT_PATHS.newServiceStatus.replace(':ref', encodeURIComponent(mutation.data.pendingRef)),
      window.location.origin,
    ).toString();

    return (
      <Alert variant="success" title={t('shop.newService.thanks.title')} role="status">
        {/* Says what actually happened. `emailSent` is false when no relay
            accepted the message, and "check your inbox" would then be a
            falsehood the customer only discovers days later. */}
        <p>
          {mutation.data.emailSent
            ? t('shop.newService.thanks.body')
            : t('shop.newService.thanks.bodyNoEmail')}
        </p>
        <p className="mt-2 break-all text-xs">
          <a href={statusUrl} className="text-primary underline">
            {t('shop.newService.thanks.ref', { url: statusUrl })}
          </a>
        </p>
      </Alert>
    );
  }

  if (!open) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-sm text-foreground/70">{t('shop.newService.prompt')}</p>
        <div>
          <Button variant="secondary" onClick={() => setOpen(true)}>
            {t('shop.newService.open')}
          </Button>
        </div>
      </div>
    );
  }

  const byUnits = premiseType !== '' && scalesByUnitCount(premiseType);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-medium text-foreground">{t('shop.newService.title')}</h2>
        <p className="text-sm text-foreground/70">{t('shop.newService.body')}</p>
      </div>

      {collectsAddress ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="shop-new-service-line1">{t('shop.newService.line1.label')}</Label>
            <Input
              id="shop-new-service-line1"
              name="line1"
              autoComplete="address-line1"
              value={line1}
              onChange={(e) => {
                setLine1(e.target.value);
                setError(undefined);
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="shop-new-service-city">{t('shop.newService.city.label')}</Label>
            <Input
              id="shop-new-service-city"
              name="city"
              autoComplete="address-level2"
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                setError(undefined);
              }}
            />
            {/* The ZIP is the one the customer already chose — shown, never re-asked,
                so it cannot disagree with the ZIP whose suggestions just missed them. */}
            <span className="text-xs text-foreground/60">
              {t('shop.newService.zip.fixed', { zip })}
            </span>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <Label htmlFor="shop-new-service-premise-type">
          {t('shop.newService.premiseType.label')}
        </Label>
        <RadioGroup
          name="shop-new-service-premise-type"
          value={premiseType}
          ariaLabel={t('shop.newService.premiseType.label')}
          onChange={(value) => {
            setPremiseType(value as NewServicePremiseType);
            setError(undefined);
          }}
        >
          {NEW_SERVICE_PREMISE_TYPES.map((type) => (
            <Radio key={type} value={type} label={t(premiseTypeLabelKey(type))} />
          ))}
        </RadioGroup>
      </div>

      {byUnits ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="shop-new-service-units">{t('shop.newService.unitCount.label')}</Label>
          <Input
            id="shop-new-service-units"
            name="unitCount"
            inputMode="numeric"
            value={unitCount}
            onChange={(e) => {
              setUnitCount(e.target.value);
              setError(undefined);
            }}
          />
          <p className="text-xs text-foreground/60">{t('shop.newService.unitCount.help')}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Label htmlFor="shop-new-service-sqft">{t('shop.newService.squareFeet.label')}</Label>
          <Input
            id="shop-new-service-sqft"
            name="squareFeet"
            inputMode="numeric"
            value={squareFeet}
            onChange={(e) => {
              setSquareFeet(e.target.value);
              setError(undefined);
            }}
          />
          <p className="text-xs text-foreground/60">{t('shop.newService.squareFeet.help')}</p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Label htmlFor="shop-new-service-occupancy">
          {t('shop.newService.occupancy.label')}
        </Label>
        <Input
          id="shop-new-service-occupancy"
          name="expectedOccupancyDate"
          type="date"
          value={expectedOccupancyDate}
          onChange={(e) => setExpectedOccupancyDate(e.target.value)}
        />
        <p className="text-xs text-foreground/60">{t('shop.newService.occupancy.help')}</p>
      </div>

      {/* US-14 — the permit is the one thing a customer can chase. Optional and
          placed last so it never blocks a shopper who has not got it yet. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor="shop-new-service-permit">{t('shop.newService.permit.label')}</Label>
          <Input
            id="shop-new-service-permit"
            name="permitNumber"
            value={permitNumber}
            onChange={(e) => setPermitNumber(e.target.value)}
          />
          <p className="text-xs text-foreground/60">{t('shop.newService.permit.help')}</p>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="shop-new-service-builder">{t('shop.newService.builder.label')}</Label>
          <Input
            id="shop-new-service-builder"
            name="builderName"
            value={builderName}
            onChange={(e) => setBuilderName(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="shop-new-service-email">{t('shop.newService.email.label')}</Label>
        <Input
          id="shop-new-service-email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder={t('shop.newService.email.placeholder')}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(undefined);
          }}
        />
      </div>

      {error !== undefined ? (
        <Alert variant="danger" role="alert">
          {t(error.messageKey)}
        </Alert>
      ) : null}

      {mutation.isError ? (
        <Alert variant="danger" role="alert">
          {t('shop.newService.error')}
        </Alert>
      ) : null}

      <div>
        <Button variant="primary" onClick={handleSubmit} disabled={mutation.isPending}>
          {mutation.isPending ? t('shop.newService.submitting') : t('shop.newService.submit')}
        </Button>
      </div>
    </div>
  );
}
